import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { Plus } from "lucide-react";
import { useState } from "react";

import { useForm } from "@tanstack/react-form";

import { AsyncPaginate } from "react-select-async-paginate";

import { addServiceSchemaSchema } from "@/zod-schemas/services/add-service-schema";
import { toServiceRequest } from "@/mappers/services/service-form-to-request";

import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RequiredInput } from "../common/required-input";
import { Separator } from "@/components/ui/separator";

import { toast } from "sonner";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  DURATION_MINUTES,
} from "@/constants/services/services-constants";

import { getServiceFormValues } from "@/default-form-values/services/default-service-form-values";

import { useCategoryLoadOptions } from "@/hooks/services/use-categories";
import { useCreateService } from "@/hooks/services/use-services";
import { useStaffLevelOptions } from "@/hooks/staff/use-staff-levels";
import { useIntl } from "react-intl";
import { durationFromMinutes } from "@/lib/utils";

export function AddService() {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const categoryOptions = useCategoryLoadOptions();
  const { data: staffLevelOptions = [] } = useStaffLevelOptions();
  const createService = useCreateService();

  const form = useForm({
    defaultValues: getServiceFormValues(),
    validators: {
      onSubmit: addServiceSchemaSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const validated = addServiceSchemaSchema.parse(value);
        await createService.mutateAsync(toServiceRequest(validated));
        toast.success(`Service "${validated.name}" created successfully`);
        form.reset();
        setOpen(false);
      } catch (error: any) {
        const statusCode = error?.response?.status;
        
        if (statusCode === 409) {
          setServerError(`A service with the name "${value.name}" already exists`);
        } else {
          toast.error(`Failed to create service "${value.name}"`);
          setOpen(false);
        }
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          form.reset();
          setServerError(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus />
          Add Service
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>New Service</DialogTitle>
        </DialogHeader>
        <Separator />

        <form
          className="mb-4"
          id="services-form"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field
              name="category"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Category
                      <RequiredInput />
                    </FieldLabel>
                    <AsyncPaginate
                      value={field.state.value}
                      onChange={field.handleChange}
                      loadOptions={categoryOptions}
                      aria-invalid={isInvalid}
                      className="w-full"
                      placeholder="Select category..."
                    />

                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />
          </FieldGroup>

          <Separator className="my-4" />

          <FieldGroup>
            <form.Field
              name="name"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Name
                      <RequiredInput />
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        setServerError(null);
                      }}
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                    {serverError && <FieldError>{serverError}</FieldError>}
                  </Field>
                );
              }}
            />
            <form.Field
              name="description"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>Description</FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />
          </FieldGroup>

          <Separator className="my-4" />

          <FieldGroup>
            <div className="font-semibold">Staff Tier Config</div>

            <div className="grid grid-cols-3 gap-4">
              <form.Field
                name="staff_level"
                children={(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>
                        Staff Level
                        <RequiredInput />
                      </FieldLabel>
                      <Select
                        name={field.name}
                        value={field.state.value}
                        onValueChange={(value) => field.handleChange(value)}
                        aria-invalid={isInvalid}
                        disabled={false}
                      >
                        <SelectTrigger id={field.name} className="w-full">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {staffLevelOptions.map((level) => (
                              <SelectItem key={level.value} value={level.value}>
                                {level.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>

                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              />

              <form.Field
                name="duration"
                children={(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>
                        Duration
                        <RequiredInput />
                      </FieldLabel>
                      <Select
                        name={field.name}
                        value={field.state.value.toString()}
                        onValueChange={(value) =>
                          field.handleChange(Number(value))
                        }
                        aria-invalid={isInvalid}
                      >
                        <SelectTrigger id={field.name} className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          <SelectGroup>
                            {DURATION_MINUTES.map((minutes) => (
                              <SelectItem
                                key={minutes}
                                value={String(minutes)}
                              >
                                {intl.formatDuration(durationFromMinutes(minutes), { format: "short" })}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              />
              <form.Field
                name="price"
                children={(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>
                        Price <RequiredInput />
                      </FieldLabel>
                      <div className="relative">
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                        >
                          $
                        </span>
                        <Input
                          id={field.name}
                          name={field.name}
                          type="number"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          aria-invalid={isInvalid}
                          autoComplete="off"
                          className="pl-6"
                        />
                      </div>
                      <FieldDescription>Inc. GST</FieldDescription>
                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              />
            </div>
          </FieldGroup>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-24">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" form="services-form" className="w-24">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
